/**
 * "Share to chat" contract — the shape of an entity being shared into the
 * internal staff chat (a sales-agent lead, a CRM prospect, or a CRM customer)
 * and the builder for the prefilled message body.
 *
 * The shared message is plain text: a one-line title, a few short context
 * lines, and an internal **deep-link path** on its own line. That path is
 * rendered as an in-app link by `segmentChatText` (chat.ts) — its applink
 * allowlist is kept in sync with the `deepLinkPath`s produced here. No PDF and
 * no change to the `chat_messages` schema. See docs/chat.md.
 *
 * NOTE (non-monetary wall): a customer subject's `summaryLines` must never
 * carry money (contract value / MRR / ARR / LTV) — same discipline as the
 * CRM → Support boundary. The per-entity adapters that build these subjects
 * enforce that; this builder just formats whatever it's given.
 */

/** A non-monetary, presentation-ready view of a shareable entity. */
export interface ShareToChatSubject {
  /** Entity-type label, e.g. "Sales lead", "Prospect", "Customer". */
  kind: string;
  /** Display name / title of the entity (company or person). */
  title: string;
  /** 0–3 short, already-formatted context lines (non-monetary). */
  summaryLines: string[];
  /** In-app deep-link path, e.g. "/crm/customers/123". Must match one of the
   * applink routes recognized by `segmentChatText`. */
  deepLinkPath: string;
}

/**
 * Build the prefilled chat-message body for a shared entity. The operator can
 * edit it before sending. Empty/whitespace summary lines are dropped so a
 * sparse entity doesn't leave blank lines in the message.
 */
export function buildShareChatMessage(subject: ShareToChatSubject): string {
  const title = subject.title.trim() || "(untitled)";
  const lines = [`📋 ${subject.kind.trim()}: ${title}`];
  for (const line of subject.summaryLines) {
    const t = line.trim();
    if (t) lines.push(t);
  }
  lines.push(subject.deepLinkPath);
  return lines.join("\n");
}
